import * as THREE from 'three';
import { SceneManager } from './scene-manager';
import { IAREngine, IARTracker, IARAnchor } from './ar-engine/ar-engine.interface';
import { TrackerConfig, TrackerState, LoadingProgressCallback } from './ar-engine/ar-types';

// Re-export TrackerConfig for backward compatibility
export type { TrackerConfig };

/**
 * Manages AR triggers and their scene associations
 */
export class TriggerManager {
  private triggers: Map<string, {
    tracker: IARTracker;
    anchor: IARAnchor;
    config: TrackerConfig;
    visibilityTimeout?: number;  // Debounce timer for visibility changes
    currentlyVisible: boolean;    // Track current visibility state
    isCurrentlyTracked: boolean;  // Is target currently being tracked by AR engine
    firstVisibleFired: boolean;   // Has onFirstVisible been called already?
  }> = new Map();

  // Multi-trigger priority system
  private visibleTriggers: Set<string> = new Set();  // All currently visible triggers
  private activeTrigger: string | null = null;       // Currently displayed trigger (highest priority)
  private visibilityListeners: Set<(payload: { id: string; config: TrackerConfig; state: TrackerState }) => void> = new Set();

  constructor(
    private arEngine: IAREngine,
    private scene: THREE.Scene,  // Note: For MindAR, this may be replaced; use arEngine.getScene() after init
    private sceneManager: SceneManager
  ) {}

  /**
   * Create and register an image trigger
   */
  async createTrigger(config: TrackerConfig, renderer: THREE.WebGLRenderer, onProgress?: LoadingProgressCallback): Promise<THREE.Group> {
    if (this.triggers.has(config.id)) {
      console.warn(`Trigger with id '${config.id}' already exists`);
      return this.triggers.get(config.id)!.anchor.getGroup();
    }

    // Create tracker using AR engine with progress callback
    const tracker = await this.arEngine.createTracker({
      ...config,
      onProgress: onProgress
    });

    // Create anchor
    const anchor = tracker.createAnchor();
    const anchorGroup = anchor.getGroup();

    // Add to scene (if not already added by the engine)
    // Use arEngine.getScene() to get the current scene (important for MindAR which creates its own scene)
    if (!anchorGroup.parent) {
      const currentScene = this.arEngine.getScene();
      currentScene.add(anchorGroup);
    }

    // Store trigger data
    this.triggers.set(config.id, {
      tracker,
      anchor,
      config,
      currentlyVisible: false,   // Start hidden
      isCurrentlyTracked: false,  // Not yet tracked
      firstVisibleFired: false
    });

    // Initialize the associated scene
    if (this.sceneManager.hasScene(config.sceneId)) {
      this.sceneManager.initScene(config.sceneId, anchorGroup, renderer);
      // Start hidden until tracker is detected
      this.sceneManager.setSceneVisible(config.sceneId, false);
    } else {
      console.error(`Scene '${config.sceneId}' not found for trigger '${config.id}'`);
    }

    // Setup visibility handlers
    this.setupVisibilityHandlers(config.id);

    console.log(`🎯 Trigger created: ${config.name} (${config.id}) → Scene: ${config.sceneId}`);

    return anchorGroup;
  }

  /**
   * Setup visibility event handlers for a trigger
   */
  private setupVisibilityHandlers(triggerId: string): void {
    console.log(`🎪 Setting up visibility handlers for trigger: ${triggerId}`);

    const trigger = this.triggers.get(triggerId);
    if (!trigger) {
      console.error(`   ❌ Trigger not found: ${triggerId}`);
      return;
    }

    const { tracker, config } = trigger;

    // Register visibility callback with the tracker
    // Use priority-based visibility with debouncing
    tracker.onVisibilityChange((id: string, state: TrackerState) => {
      const trigger = this.triggers.get(triggerId);
      if (!trigger) return;

      const isVisible = state === TrackerState.VISIBLE;

      console.log(`📷 ========== VISIBILITY CHANGE: ${config.name} ==========`);
      console.log(`   Trigger ID: ${id}`);
      console.log(`   State: ${isVisible ? 'VISIBLE' : 'NOT_VISIBLE'}`);
      console.log(`   Priority: ${config.priority ?? 0}`);
      console.log(`   Scene ID: ${config.sceneId}`);

      // Clear any pending visibility change
      if (trigger.visibilityTimeout) {
        clearTimeout(trigger.visibilityTimeout);
      }

      if (isVisible) {
        if (config.onFirstVisible && !trigger.firstVisibleFired) {
          trigger.firstVisibleFired = true;
          try {
            config.onFirstVisible();
          } catch (err) {
            console.error(`   ⚠️ onFirstVisible handler error for ${config.id}`, err);
          }
        }

        // Target found - add to visible set immediately
        console.log(`   ✅ Adding to visible triggers`);
        this.visibleTriggers.add(triggerId);
        trigger.isCurrentlyTracked = true;
        this.notifyVisibilityListeners({ id: triggerId, config, state });

        // Re-evaluate which scene should be active
        this.updateActiveScene();
      } else {
        // Target lost - remove from visible set with debounce
        console.log(`   ⏳ Scheduling removal from visible triggers (debounced 200ms)`);
        trigger.visibilityTimeout = setTimeout(() => {
          console.log(`   ❌ Removing from visible triggers`);
          this.visibleTriggers.delete(triggerId);
          trigger.isCurrentlyTracked = false;
          this.notifyVisibilityListeners({ id: triggerId, config, state });

          // Re-evaluate which scene should be active
          this.updateActiveScene();
        }, 200);
      }
    });

    console.log(`   ✅ Visibility handlers registered for: ${triggerId}`);
  }

  /**
   * Update which scene is active based on priority
   * Finds the highest priority visible trigger and shows only its scene
   */
  private updateActiveScene(): void {
    // Find highest priority visible trigger
    let highestPriority = -Infinity;
    let highestPriorityTrigger: string | null = null;

    for (const triggerId of this.visibleTriggers) {
      const trigger = this.triggers.get(triggerId);
      if (trigger && trigger.isCurrentlyTracked) {
        const priority = trigger.config.priority ?? 0;
        if (priority > highestPriority) {
          highestPriority = priority;
          highestPriorityTrigger = triggerId;
        }
      }
    }

    // If active trigger changed, update visibility
    if (highestPriorityTrigger !== this.activeTrigger) {
      console.log(`🔄 Active trigger changed: ${this.activeTrigger || 'none'} → ${highestPriorityTrigger || 'none'}`);

      // Hide old active scene
      if (this.activeTrigger) {
        const oldTrigger = this.triggers.get(this.activeTrigger);
        if (oldTrigger) {
          console.log(`   🚫 Hiding scene: ${oldTrigger.config.sceneId} (${oldTrigger.config.name})`);
          this.sceneManager.setSceneVisible(oldTrigger.config.sceneId, false);
          oldTrigger.currentlyVisible = false;
        }
      }

      // Show new active scene
      if (highestPriorityTrigger) {
        const newTrigger = this.triggers.get(highestPriorityTrigger);
        if (newTrigger) {
          console.log(`   👁️ Showing scene: ${newTrigger.config.sceneId} (${newTrigger.config.name}, priority: ${newTrigger.config.priority ?? 0})`);
          this.sceneManager.setSceneVisible(newTrigger.config.sceneId, true);
          newTrigger.currentlyVisible = true;
        }
      }

      this.activeTrigger = highestPriorityTrigger;
    }
  }

  /**
   * Get trigger anchor group by ID
   */
  getTrigger(id: string): THREE.Group | undefined {
    return this.triggers.get(id)?.anchor.getGroup();
  }

  /**
   * List all triggers
   */
  listTriggers(): Array<{ id: string; name: string; sceneId: string }> {
    return Array.from(this.triggers.values()).map(t => ({
      id: t.config.id,
      name: t.config.name,
      sceneId: t.config.sceneId
    }));
  }

  /**
   * Bind a different scene to an existing trigger
   */
  rebindScene(triggerId: string, newSceneId: string, renderer: THREE.WebGLRenderer, onProgress?: LoadingProgressCallback): boolean {
    const trigger = this.triggers.get(triggerId);
    if (!trigger) {
      console.error(`Trigger '${triggerId}' not found`);
      return false;
    }

    if (!this.sceneManager.hasScene(newSceneId)) {
      console.error(`Scene '${newSceneId}' not found`);
      return false;
    }

    // Dispose old scene
    this.sceneManager.disposeScene(trigger.config.sceneId);

    // Update config
    trigger.config.sceneId = newSceneId;

    // Initialize new scene
    this.sceneManager.initScene(newSceneId, trigger.anchor.getGroup(), renderer);

    console.log(`🔄 Trigger '${triggerId}' rebound to scene '${newSceneId}'`);
    return true;
  }

  /**
   * Remove a trigger
   */
  removeTrigger(id: string): void {
    const trigger = this.triggers.get(id);
    if (!trigger) {
      console.error(`Trigger '${id}' not found`);
      return;
    }

    // Clear any pending visibility timeout
    if (trigger.visibilityTimeout) {
      clearTimeout(trigger.visibilityTimeout);
    }

    // Dispose the scene
    this.sceneManager.disposeScene(trigger.config.sceneId);

    // Dispose the anchor
    trigger.anchor.dispose();

    // Remove tracker from AR engine
    this.arEngine.removeTracker(id);

    // Remove from map
    this.triggers.delete(id);

    console.log(`🗑️ Trigger removed: ${trigger.config.name}`);
  }

  /**
   * Remove all triggers
   */
  removeAll(): void {
    for (const [id] of this.triggers) {
      this.removeTrigger(id);
    }
  }

  /**
   * Subscribe to visibility changes (useful for HUD/telemetry overlays)
   */
  onVisibilityChange(callback: (payload: { id: string; config: TrackerConfig; state: TrackerState }) => void): void {
    this.visibilityListeners.add(callback);
  }

  private notifyVisibilityListeners(payload: { id: string; config: TrackerConfig; state: TrackerState }): void {
    this.visibilityListeners.forEach(cb => cb(payload));
  }
}
